import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { faqAPI } from '@/services/api';

const SESSION_KEY = 'equimarket_faq_session_id';

function getSessionId() {
	try {
		let sid = localStorage.getItem(SESSION_KEY);
		if (!sid) {
			sid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).slice(0, 64);
			localStorage.setItem(SESSION_KEY, sid);
		}
		return sid;
	} catch {
		return `anon-${Date.now()}`;
	}
}

const VOTES_KEY = 'equimarket_faq_votes';

function getMyVote(faqId) {
	try {
		const map = JSON.parse(localStorage.getItem(VOTES_KEY) || '{}');
		return map[faqId] ?? null;
	} catch {
		return null;
	}
}

function rememberMyVote(faqId, helpful) {
	try {
		const map = JSON.parse(localStorage.getItem(VOTES_KEY) || '{}');
		map[faqId] = helpful;
		localStorage.setItem(VOTES_KEY, JSON.stringify(map));
	} catch {
		// ignore
	}
}

export default function FAQFeedback({ faqId }) {
	const { t } = useTranslation();
	const [myVote, setMyVote] = useState(() => getMyVote(faqId));
	const [showCommentBox, setShowCommentBox] = useState(false);
	const [comment, setComment] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [commentSent, setCommentSent] = useState(false);

	const sendVote = async (helpful) => {
		setSubmitting(true);
		try {
			await faqAPI.submitFeedback(faqId, { helpful, session_id: getSessionId() });
			rememberMyVote(faqId, helpful);
			setMyVote(helpful);
			// Only ask for a comment after a "Not helpful" — that's where we want to learn why
			if (helpful === false) setShowCommentBox(true);
		} catch {
			// silently ignore; non-critical UX
		} finally {
			setSubmitting(false);
		}
	};

	const sendComment = async () => {
		if (!comment.trim()) return;
		setSubmitting(true);
		try {
			await faqAPI.submitFeedback(faqId, {
				helpful: myVote === true,
				session_id: getSessionId(),
				comment: comment.trim(),
			});
			setCommentSent(true);
			setShowCommentBox(false);
		} catch {
			// ignore
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div
			className="mt-5 pt-4 border-t border-[#E6E2D6]/70 flex flex-col gap-3"
			data-testid={`faq-feedback-${faqId}`}
		>
			<div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
          {t('faq.helpful_question')}
        </span>
				<div className="flex items-center gap-2">
					<button
						type="button"
						disabled={submitting}
						onClick={() => sendVote(true)}
						aria-pressed={myVote === true}
						className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border transition-colors ${
							myVote === true
								? 'bg-[#1A3C34] text-white border-[#1A3C34]'
								: 'bg-white text-[#1A3C34] border-[#E6E2D6] hover:bg-[#F2F0E9]'
						}`}
						data-testid={`faq-feedback-yes-${faqId}`}
					>
						<ThumbsUp className="w-3.5 h-3.5" /> {t('faq.helpful_yes')}
					</button>
					<button
						type="button"
						disabled={submitting}
						onClick={() => sendVote(false)}
						aria-pressed={myVote === false}
						className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border transition-colors ${
							myVote === false
								? 'bg-[#9B2C2C] text-white border-[#9B2C2C]'
								: 'bg-white text-[#1A3C34] border-[#E6E2D6] hover:bg-[#F2F0E9]'
						}`}
						data-testid={`faq-feedback-no-${faqId}`}
					>
						<ThumbsDown className="w-3.5 h-3.5" /> {t('faq.helpful_no')}
					</button>
				</div>
				{myVote !== null && !showCommentBox && (
					<span
						className="text-xs text-[#1A3C34]/80 inline-flex items-center gap-1"
						data-testid={`faq-feedback-thanks-${faqId}`}
					>
            <Check className="w-3.5 h-3.5 text-[#1A3C34]" /> {t('faq.feedback_thanks')}
          </span>
				)}
			</div>

			{showCommentBox && !commentSent && (
				<div className="flex flex-col gap-2 max-w-xl">
					<Textarea
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						maxLength={500}
						rows={3}
						placeholder={t('faq.feedback_comment_placeholder')}
						className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm"
						data-testid={`faq-feedback-comment-${faqId}`}
					/>
					<div className="flex items-center justify-end gap-2">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setShowCommentBox(false)}
							className="text-[#666055] h-8 text-xs"
							data-testid={`faq-feedback-skip-${faqId}`}
						>
							{t('faq.feedback_skip')}
						</Button>
						<Button
							size="sm"
							onClick={sendComment}
							disabled={!comment.trim() || submitting}
							className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-8 text-xs"
							data-testid={`faq-feedback-submit-${faqId}`}
						>
							{t('faq.feedback_submit')}
						</Button>
					</div>
				</div>
			)}

			{commentSent && (
				<span
					className="text-xs text-[#1A3C34]/80 inline-flex items-center gap-1"
					data-testid={`faq-feedback-comment-sent-${faqId}`}
				>
          <Check className="w-3.5 h-3.5 text-[#1A3C34]" /> {t('faq.feedback_comment_thanks')}
        </span>
			)}
		</div>
	);
}